/**
 * @Author: zhangxiaojian
 * @Date: 2025/8/15
 * @LastEditors: zhangxiaojian
 * @Description 全局事件总线，跨微应用通信，一旦发布了事件，则其他微应用都可以监听到该事件，并执行相应的方法，
 *              注意事项：1. 为避免出现内存泄露问题，在组件销毁时，请调用$off方法取消订阅事件
 *                      2. 请将定义的事件名称统一定义在GlobalEvent.js中，便于统一管理，切勿自行在组件内添加字符串事件名称，GlobalEvent.js中可直接是枚举值，也可按模块定义枚举值
 *                          {
 *                            "OPEN_WINDOW": "OPEN_WINDOW",
 *                            "MEDIAX":{
 *                              "OPEN_WINDOW": "OPEN_WINDOW",
 *                            }
 *                          }
 *
 **/

import Vue from 'vue'
import GlobalEvent, { EventBusDataKey } from './GlobalEvent'

/**
 * 获取全局响应式数据key列表
 * @param obj 响应式集合
 * @returns {*[]}
 */
const getEventBusDataKeys = (obj) => {
  return Object.keys(obj).map((item) => {
    if (typeof obj[item] === 'string') {
      // 值为字符串，则直接返回
      return obj[item]
    } else {
      // 非字符串，则进行递归
      return getEventBusDataKeys(obj[item]).flat()
    }
  })
}

// 全局事件总线
class EventBus {
  // 事件总线
  events = null
  constructor() {
    // 默认使用全局事件总线，避免重复创建
    let tempEventBus = window.GlobalEventBus
    if (!tempEventBus) {
      tempEventBus = new Vue({
        data() {
          return {
            eventKeyList: [], // 用户存储订阅的事件列表
            data: {
              AUTH: { // 用户权限相关数据
                  AUTHS: null, // 用户权限数组
                  MODS: null, // 用户权限模块集合
                  GROUPS: null, // 用户权限模块分组集合
                  USER: null, // 用户相关信息
                  AI_ASSISTANT_SWITCH:0,// 是否开启运营智能助手
                  FIRST_GROUPS_IS_SYSTEM: false // 是否是系统管理页面
              },
              PREFERENCESELECTORS: { // 平台配置和个人偏好相关参数 目前只用到了自定义样式的字体和字号（不推荐复用和追加 需求完善后会统一类名
                PREFERENCE_FONTFAMILY_SELECTORS: [
                  '.content-app-publish-container .com-e5-table__line__field-SYS_TOPIC .SYS_TOPIC, .p-group__row .col-topic, .p-group__card .col-card-title, .popover_content .article-topic',
                  '.root-article-table .com-e5-table__line__field-SYS_TOPIC, .article-edit-table .com-e5-table__line__field-SYS_TOPIC, .audit-article-table  .com-e5-table__line__field-SYS_TOPIC',
                  '.journal-column-table__table .col-SYS_TOPIC .journal-column-table__text, .journal-column-tuce-table .tp-group-wrapper .col-card-title'
                ], // 平台配置和个人偏好字体或字体包影响的dom容器（默认包括稿件列表和快速浏览
                PREFERENCE_FONTSIZE_SELECTORS: ['.oldContent.previewBoxContent'], // 个人偏好设置自定义字号影响的dom容器（默认包括快速浏览的正文部分
              },
              SECRET: { // 密钥相关
                SM2_PUB_KEY: '', // 本地生成的sm2加密的公钥
                SM2_PRI_KEY: '', // 本地生成的sm2加密的私钥
                SM4_PRI_KEY: '', // 服务端用于加密的sm4私钥
              },
              PUGC: {
                APP_PUB_KEY: '', // pugc登录时生成的假公钥
                APP_PRI_KEY: '', // pugc登录时生成的假私钥
                TOKEN_DATA: '', // pugc登录时生成的token数据
              },
            }
          }
        }
      })
      window.GlobalEventBus = tempEventBus
    }
    this.events = tempEventBus
  }

  /**
   * 增加监听订阅方法，注意：此处的callback方法，请勿使用匿名函数，否则无法移除,使用方法如下
   * const callback = function(data){}
   * EventBus.$on('OPEN_WINDOW', callback)
   * @param eventName 事件名称
   * @param callback 回调方法
   */
  $on(eventName, callback) {
    // 存储事件
    if (!this.events.eventKeyList[eventName]) {
      this.events.eventKeyList[eventName] = []
    }
    let GlobalEventArr = Object.keys(GlobalEvent).map(
      (item) => GlobalEvent[item]
    )
    if (!GlobalEventArr.includes(eventName)) {
      // 监听的值不在枚举中，进行警告提示
      console.warn('为保证监听数据统一管理，请勿自行添加监听事件名称')
    }
    this.events.eventKeyList[eventName].push(callback)
    this.events.$on(eventName, callback)
  }
  /**
   * 增加发布方法
   * @param eventName 事件名称
   * @param args 参数
   */
  $emit(eventName, ...args) {
    this.events.$emit(eventName, ...args)
  }
  /**
   * 移除监听订阅方法
   * @param eventName 事件名称
   * @param callback 回调方法
   */
  $off(eventName, callback) {
    // 移除事件
    if (this.events.eventKeyList[eventName]) {
      this.events.eventKeyList[eventName] = this.events.eventKeyList[
        eventName
      ].filter((item) => item !== callback)
    }
    this.events.$off(eventName, callback)
  }

  /**
   * 一次订阅事件
   * @param eventName 事件名称
   * @param callback 回调方法
   */
  $once(eventName, callback) {
    this.events.$once(eventName, callback)
  }
  /**
   * 挂载到Vue原型上
   * @param VueObj
   */
  install(VueObj) {
    VueObj.prototype.$eventBus = this
  }

  /**
   * 获取订阅了的事件列表
   * @param eventName 事件名称
   * @returns {*|*[]}
   */
  getEventList(eventName) {
    return this.events.eventKeyList[eventName] || []
  }

  /**
   * 设置全局响应式数据函数，为保证key的统一管理，数据key需要在GlobalEvent中定义，否则无法设置值
   * @param key 数据key
   * @param val 数据值
   */
  setData(key, val) {
    let EventBusDataKeyArr = getEventBusDataKeys(EventBusDataKey).flat()
    if (!EventBusDataKeyArr.includes(key)) {
      // 设置的key不在枚举中，进行警告提示
      console.error('设置数据值，需要先在GlobalEvent中定义数据key值')
      return
    }
    try{
      let keyArr = key.split('.')
      let current = this.events.$data.data
      // 遍历到倒数第二层
      for (let i = 0; i < keyArr.length - 1; i++) {
        current = current[keyArr[i]]
      }
      this.events.$set(current, keyArr[keyArr.length -1], val)
    }catch (e) {
      console.error(e)
    }

  }

  /**
   * 获取全局响应式数据函数
   * @param key 数据key
   * @returns {*}
   */
  getData(key) {
    try {
      let keyArr = key.split('.')
      let val = this.events.$data.data[keyArr[0]]
      keyArr.forEach((item, index) => {
        if (index > 0 && val) {
          val = val[item]
        }
      })
      return val
    } catch (e) {
      return undefined
    }
  }

}

export default new EventBus()
